import { useEffect, useMemo, useRef, useState } from 'react';
import { api, extractErrorMessage } from '../api/client';
import { Button } from './Button';
import { useToast } from './Toast';

// Formato de la planilla de pedido de un proveedor: cada proveedor manda la suya y acá se dice, una vez,
// en qué columna está cada dato. Lo lee el backend (compras/import-specs/formato-planilla-oc.ts).

export const CAMPOS = [
  'codigo', 'cantidad', 'descripcion', 'precioLista', 'precioNeto', 'descuento1', 'descuento2', 'descuento3', 'ean', 'unidadesPorBulto',
] as const;
export type Campo = typeof CAMPOS[number];

export const ETIQUETA_CAMPO: Record<Campo, string> = {
  codigo: 'Código del proveedor',
  cantidad: 'Cantidad pedida',
  descripcion: 'Descripción',
  precioLista: 'Precio de lista',
  precioNeto: 'Precio neto (con descuentos)',
  descuento1: 'Descuento 1',
  descuento2: 'Descuento 2',
  descuento3: 'Descuento 3',
  ean: 'Código de barras (EAN)',
  unidadesPorBulto: 'Unidades por bulto',
};
const OBLIGATORIOS: Campo[] = ['codigo', 'cantidad'];

export interface Encabezado { indice: number; texto: string; clave: string; ocurrencia: number }
/** Cómo escribe el proveedor los descuentos: fracción (0,15 = 15%) o porcentaje (15 = 15%). */
export type EscalaDescuentos = 'fraccion' | 'porcentaje';
interface ColumnaFormato { encabezado: string; ocurrencia: number; indice: number }
export interface FormatoPlanilla {
  hoja: string | null;
  filaEncabezado: number | null;
  columnas: Partial<Record<Campo, ColumnaFormato>>;
  encabezadosConocidos: Encabezado[];
  opciones: { cantidadEnBultos: boolean; preciosConIva: boolean; celdasDescuentoGeneral: string[]; escalaDescuentos?: EscalaDescuentos };
}
interface FilaLeida { filaExcel: number; codigo: string; ean: string; cantidad: number; precio: number }
interface ErrorFila { fila: number; mensaje: string }
export interface AnalisisPlanilla {
  hojas: string[];
  hoja: string | null;
  filaEncabezado: number | null;
  encabezados: Encabezado[];
  filasEjemplo: Array<{ fila: number; celdas: string[] }>;
  sugerencia: Partial<Record<Campo, ColumnaFormato>>;
  formatoGuardado: FormatoPlanilla | null;
  vistaPrevia: { filas: FilaLeida[]; errores: ErrorFila[]; total: number } | null;
  /** Detectada por los valores de las columnas de descuento (todos hasta 1 → fracción). */
  escalaDescuentos: EscalaDescuentos;
}

const URL_ANALIZAR = '/api/v3/compras/planilla-proveedor/analizar';
export const urlFormato = (proveedorId: string) => `/api/v3/proveedores/${proveedorId}/formato-planilla-oc`;

/** Letra de la columna de Excel (1 = A, 27 = AA). */
export function letraColumna(indice: number): string {
  let s = '';
  for (let n = indice; n > 0; n = Math.floor((n - 1) / 26)) s = String.fromCharCode(65 + ((n - 1) % 26)) + s;
  return s;
}

const nombreColumna = (e: Encabezado) => `${letraColumna(e.indice)} · ${e.texto}${e.ocurrencia > 1 ? ` (${e.ocurrencia}ª vez)` : ''}`;
const mismo = (c: ColumnaFormato, e: Encabezado) => c.encabezado === e.clave && c.ocurrencia === e.ocurrencia;
const pesos = (n: number) => `$${n.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const ESPERA_VISTA_PREVIA_MS = 300;

const analizar = (archivo: File, campos: Record<string, string>) => api.upload<AnalisisPlanilla>(URL_ANALIZAR, archivo, campos);

// ── Configurar el formato ────────────────────────────────────────────────

interface ConfigurarProps {
  proveedorId: string;
  proveedorNombre?: string;
  archivo: File;
  puedeGuardar: boolean;
  onGuardado: () => void;
  onVolver: () => void;
}

export function ConfigurarFormatoPlanilla({ proveedorId, proveedorNombre, archivo, puedeGuardar, onGuardado, onVolver }: ConfigurarProps) {
  const toast = useToast();
  const [analisis, setAnalisis] = useState<AnalisisPlanilla | null>(null);
  const [error, setError] = useState('');
  const [hoja, setHoja] = useState('');
  // Fila de títulos elegida a mano (si no, la detecta el sistema cada vez)
  const [filaManual, setFilaManual] = useState<number | null>(null);
  // Fila fija del formato guardado (la usa el backend al analizar; se conserva al guardar)
  const [filaGuardada, setFilaGuardada] = useState<number | null>(null);
  const [columnas, setColumnas] = useState<Partial<Record<Campo, number>>>({});
  const [cantidadEnBultos, setCantidadEnBultos] = useState(false);
  const [preciosConIva, setPreciosConIva] = useState(false);
  const [celdas, setCeldas] = useState('');
  const [escala, setEscala] = useState<EscalaDescuentos>('fraccion');
  // Elegida a mano (o la del formato guardado): ya no la cambia la detección
  const [escalaElegida, setEscalaElegida] = useState(false);
  const [vista, setVista] = useState<AnalisisPlanilla['vistaPrevia']>(null);
  const [guardando, setGuardando] = useState(false);
  const primeraVez = useRef(true);
  const escalaElegidaRef = useRef(false);
  escalaElegidaRef.current = escalaElegida;
  const pedidoVista = useRef(0);

  useEffect(() => {
    let vigente = true;
    setError('');
    analizar(archivo, { proveedorId, hoja, filaEncabezado: filaManual ? String(filaManual) : '' })
      .then(a => {
        if (!vigente) return;
        setAnalisis(a);
        const guardado = primeraVez.current ? a.formatoGuardado : null;
        const base = guardado?.columnas ?? a.sugerencia;
        const elegidas: Partial<Record<Campo, number>> = {};
        for (const campo of CAMPOS) {
          const c = base[campo];
          const e = c && a.encabezados.find(x => mismo(c, x));
          if (e) elegidas[campo] = e.indice;
        }
        setColumnas(elegidas);
        const escalaGuardada = guardado?.opciones.escalaDescuentos;
        if (escalaGuardada) setEscalaElegida(true);
        if (escalaGuardada || !escalaElegidaRef.current) setEscala(escalaGuardada ?? a.escalaDescuentos);
        if (guardado) {
          setCantidadEnBultos(guardado.opciones.cantidadEnBultos);
          setPreciosConIva(guardado.opciones.preciosConIva);
          setCeldas(guardado.opciones.celdasDescuentoGeneral.join(', '));
          setFilaGuardada(guardado.filaEncabezado);
        }
        primeraVez.current = false;
      })
      .catch(e => { if (vigente) setError(extractErrorMessage(e)); });
    return () => { vigente = false; };
  }, [archivo, proveedorId, hoja, filaManual]);

  const borrador = useMemo<FormatoPlanilla | null>(() => {
    if (!analisis) return null;
    const cols: FormatoPlanilla['columnas'] = {};
    for (const [campo, indice] of Object.entries(columnas) as Array<[Campo, number]>) {
      const e = analisis.encabezados.find(x => x.indice === indice);
      if (e) cols[campo] = { encabezado: e.clave, ocurrencia: e.ocurrencia, indice };
    }
    return {
      hoja: analisis.hoja,
      filaEncabezado: filaManual ?? filaGuardada,
      columnas: cols,
      encabezadosConocidos: analisis.encabezados,
      opciones: { cantidadEnBultos, preciosConIva, celdasDescuentoGeneral: celdas.split(/[\s,;]+/).filter(Boolean), escalaDescuentos: escala },
    };
  }, [analisis, columnas, filaManual, filaGuardada, cantidadEnBultos, preciosConIva, celdas, escala]);

  // Vista previa: cómo se leen las filas con lo elegido hasta ahora (la arma el backend con el borrador)
  useEffect(() => {
    if (!borrador) return;
    const n = ++pedidoVista.current;
    const t = setTimeout(() => {
      analizar(archivo, {
        proveedorId, hoja: borrador.hoja ?? '', filaEncabezado: borrador.filaEncabezado ? String(borrador.filaEncabezado) : '',
        formato: JSON.stringify(borrador),
      })
        .then(a => {
          if (n !== pedidoVista.current) return;
          setVista(a.vistaPrevia);
          // Con otras columnas de descuento la escala detectada puede cambiar
          if (!escalaElegidaRef.current) setEscala(a.escalaDescuentos);
        })
        .catch(() => { /* la vista previa es una ayuda: si falla, guardar valida igual */ });
    }, ESPERA_VISTA_PREVIA_MS);
    return () => clearTimeout(t);
  }, [borrador, archivo, proveedorId]);

  const faltan = OBLIGATORIOS.filter(c => !columnas[c]);
  const guardar = async () => {
    if (!borrador) return;
    setGuardando(true);
    try {
      await api.put(urlFormato(proveedorId), borrador);
      toast.addToast('Formato guardado', 'success');
      onGuardado();
    } catch (e) {
      toast.addToast(extractErrorMessage(e), 'error');
    } finally {
      setGuardando(false);
    }
  };

  if (error) {
    return (
      <div className="space-y-4">
        <p role="alert" className="rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</p>
        <div className="flex justify-end"><Button variant="ghost" onClick={onVolver}>Volver</Button></div>
      </div>
    );
  }
  if (!analisis) return <p className="py-8 text-center text-sm text-slate-500">Leyendo la planilla…</p>;

  const columnasVisibles = analisis.encabezados.length
    ? Math.max(...analisis.encabezados.map(e => e.indice))
    : Math.max(0, ...analisis.filasEjemplo.map(f => f.celdas.length));
  const tituloDe = (indice: number) => analisis.encabezados.find(e => e.indice === indice)?.texto ?? '';

  return (
    <div className="space-y-5">
      <p className="text-sm text-slate-600">
        Decinos en qué columna está cada dato de la planilla{proveedorNombre ? ` de ${proveedorNombre}` : ''}. Se configura una vez:
        después cada planilla de este proveedor se importa sola.
      </p>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {analisis.hojas.length > 1 && (
          <div>
            <label htmlFor="fp-hoja" className="mb-1 block text-sm font-medium">Hoja del pedido</label>
            <select id="fp-hoja" value={analisis.hoja ?? ''} onChange={e => { setHoja(e.target.value); setFilaManual(null); }} className="w-full rounded-lg border px-3 py-2">
              {analisis.hojas.map(h => <option key={h} value={h}>{h}</option>)}
            </select>
          </div>
        )}
        <div>
          <label htmlFor="fp-fila" className="mb-1 block text-sm font-medium">Fila de los títulos</label>
          <input id="fp-fila" type="number" min={1} value={analisis.filaEncabezado ?? ''}
            onChange={e => { const n = parseInt(e.target.value, 10); setFilaManual(n > 0 ? n : null); }}
            className="w-full rounded-lg border px-3 py-2" />
          <p className="mt-1 text-xs text-slate-500">
            {analisis.filaEncabezado ? 'La encontramos sola; cambiala si no es la correcta.' : 'No encontramos la fila de los títulos: escribila.'}
          </p>
        </div>
      </div>

      <section aria-label="Planilla" className="overflow-x-auto rounded-lg border">
        <table className="min-w-full text-xs tabular-nums">
          <thead className="bg-slate-50 text-left text-slate-700">
            <tr>
              <th className="px-2 py-1.5 font-medium text-slate-500">Fila</th>
              {Array.from({ length: columnasVisibles }, (_, i) => (
                <th key={i} className="whitespace-nowrap px-2 py-1.5 font-medium">
                  <span className="text-slate-500">{letraColumna(i + 1)}</span> {tituloDe(i + 1)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {analisis.filasEjemplo.slice(0, 8).map(f => (
              <tr key={f.fila} className="border-t">
                <td className="px-2 py-1 text-slate-500">{f.fila}</td>
                {Array.from({ length: columnasVisibles }, (_, i) => (
                  <td key={i} className="max-w-[14rem] truncate whitespace-nowrap px-2 py-1">{f.celdas[i]}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <fieldset className="grid grid-cols-1 gap-x-4 gap-y-3 sm:grid-cols-2">
        <legend className="mb-2 text-sm font-semibold text-slate-800">Qué columna trae cada dato</legend>
        {CAMPOS.map(campo => (
          <div key={campo}>
            <label htmlFor={`fp-${campo}`} className="mb-1 block text-sm font-medium">
              {ETIQUETA_CAMPO[campo]}{OBLIGATORIOS.includes(campo) && <span className="text-red-600"> *</span>}
            </label>
            <select id={`fp-${campo}`} value={columnas[campo] ?? ''} aria-required={OBLIGATORIOS.includes(campo)}
              onChange={e => setColumnas({ ...columnas, [campo]: e.target.value ? Number(e.target.value) : undefined })}
              className="w-full rounded-lg border px-3 py-2 text-sm">
              <option value="">No está en la planilla</option>
              {analisis.encabezados.map(e => <option key={e.indice} value={e.indice}>{nombreColumna(e)}</option>)}
            </select>
          </div>
        ))}
      </fieldset>

      <fieldset className="space-y-3">
        <legend className="mb-2 text-sm font-semibold text-slate-800">Cómo vienen los datos</legend>
        <label className="flex items-start gap-2 text-sm">
          <input type="checkbox" className="mt-0.5" checked={cantidadEnBultos} onChange={e => setCantidadEnBultos(e.target.checked)} />
          <span>La cantidad viene en bultos <span className="text-slate-500">(se multiplica por las unidades por bulto)</span></span>
        </label>
        {cantidadEnBultos && !columnas.unidadesPorBulto && (
          <p className="text-xs text-red-700">Elegí arriba la columna de unidades por bulto.</p>
        )}
        <label className="flex items-start gap-2 text-sm">
          <input type="checkbox" className="mt-0.5" checked={preciosConIva} onChange={e => setPreciosConIva(e.target.checked)} />
          <span>Los precios incluyen IVA <span className="text-slate-500">(al importar se les saca el IVA de cada artículo)</span></span>
        </label>
        <div className="max-w-sm">
          <label htmlFor="fp-escala" className="mb-1 block text-sm font-medium">Los descuentos vienen como</label>
          <select id="fp-escala" value={escala} onChange={e => { setEscala(e.target.value as EscalaDescuentos); setEscalaElegida(true); }}
            className="w-full rounded-lg border px-3 py-2 text-sm">
            <option value="fraccion">Fracción (0,15 = 15%)</option>
            <option value="porcentaje">Porcentaje (15 = 15%)</option>
          </select>
          <p className="mt-1 text-xs text-slate-500">Lo detectamos por los valores de la planilla. Si la celda dice «15%», se lee 15% igual.</p>
        </div>
        <div className="max-w-sm">
          <label htmlFor="fp-celdas" className="mb-1 block text-sm font-medium">Celdas con un descuento para todo el pedido</label>
          <input id="fp-celdas" value={celdas} onChange={e => setCeldas(e.target.value)} placeholder="Ej.: G4" className="w-full rounded-lg border px-3 py-2 text-sm" />
          <p className="mt-1 text-xs text-slate-500">Se usa en las filas que no traen descuento propio. Si son varias, separalas con coma.</p>
        </div>
      </fieldset>

      <section aria-label="Así se va a leer" className="space-y-2">
        <h3 className="text-sm font-semibold text-slate-800">Así se va a leer</h3>
        {!vista && <p className="text-sm text-slate-500">Armando la vista previa…</p>}
        {vista && (
          <>
            <p className="text-sm text-slate-600">
              {vista.total === 1 ? '1 fila pedida' : `${vista.total} filas pedidas`} (las que tienen cantidad).
              {preciosConIva && ' Precios con IVA: al importar se les saca el IVA de cada artículo.'}
            </p>
            {vista.filas.length > 0 && (
              <div className="overflow-x-auto rounded-lg border">
                <table className="min-w-full text-sm tabular-nums">
                  <thead className="bg-slate-50 text-left text-slate-700">
                    <tr>
                      <th className="px-3 py-1.5 font-medium">Fila</th>
                      <th className="px-3 py-1.5 font-medium">Código</th>
                      <th className="px-3 py-1.5 text-right font-medium">Unidades</th>
                      <th className="px-3 py-1.5 text-right font-medium">Precio para la OC</th>
                    </tr>
                  </thead>
                  <tbody>
                    {vista.filas.map(f => (
                      <tr key={f.filaExcel} className="border-t">
                        <td className="px-3 py-1 text-slate-500">{f.filaExcel}</td>
                        <td className="px-3 py-1">{f.codigo || f.ean}</td>
                        <td className="px-3 py-1 text-right">{f.cantidad.toLocaleString('es-AR')}</td>
                        <td className="px-3 py-1 text-right">{pesos(f.precio)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {vista.errores.length > 0 && (
              <ul className="space-y-1 rounded-lg bg-red-50 p-3 text-xs text-red-700">
                {vista.errores.map((e, i) => <li key={i}>{e.fila > 0 ? `Fila ${e.fila}: ` : ''}{e.mensaje}</li>)}
              </ul>
            )}
          </>
        )}
      </section>

      <div className="flex justify-end gap-3 border-t pt-3">
        <Button variant="ghost" onClick={onVolver}>Volver</Button>
        <Button onClick={guardar} loading={guardando} disabled={guardando || faltan.length > 0 || !puedeGuardar}
          title={!puedeGuardar ? 'No tenés permiso para realizar esta acción' : faltan.length ? `Elegí la columna de: ${faltan.map(c => ETIQUETA_CAMPO[c].toLowerCase()).join(' y ')}` : undefined}>
          {guardando ? 'Guardando...' : 'Guardar formato'}
        </Button>
      </div>
    </div>
  );
}

// ── Columnas nuevas en una planilla de un proveedor con formato ─────────────

interface ColumnasNuevasProps {
  proveedorId: string;
  archivo: File;
  columnasNuevas: Encabezado[];
  puedeGuardar: boolean;
  onListo: () => void;
  /** Sin permiso para guardar: seguir con la importación sin guardar nada. */
  onIgnorar: () => void;
  onCancelar: () => void;
}

/** La planilla trae columnas que el formato no conocía: por cada una, ignorarla o usarla para un dato. Se guarda en el formato. */
export function ColumnasNuevasPlanilla({ proveedorId, archivo, columnasNuevas, puedeGuardar, onListo, onIgnorar, onCancelar }: ColumnasNuevasProps) {
  const toast = useToast();
  const [decision, setDecision] = useState<Record<number, Campo | ''>>({});
  const [guardando, setGuardando] = useState(false);

  const guardar = async () => {
    setGuardando(true);
    try {
      // Títulos de hoy: el formato pasa a conocerlos (los ignorados también) y sigue cada dato por su título
      const a = await analizar(archivo, { proveedorId });
      const guardado = a.formatoGuardado;
      if (!guardado) throw new Error('Este proveedor no tiene formato guardado.');
      const columnas: FormatoPlanilla['columnas'] = {};
      for (const campo of CAMPOS) {
        const c = guardado.columnas[campo];
        const e = c && a.encabezados.find(x => mismo(c, x));
        if (e) columnas[campo] = { encabezado: e.clave, ocurrencia: e.ocurrencia, indice: e.indice };
      }
      for (const nueva of columnasNuevas) {
        const campo = decision[nueva.indice];
        const e = campo && a.encabezados.find(x => x.clave === nueva.clave && x.ocurrencia === nueva.ocurrencia);
        if (campo && e) columnas[campo] = { encabezado: e.clave, ocurrencia: e.ocurrencia, indice: e.indice };
      }
      await api.put(urlFormato(proveedorId), { ...guardado, columnas, encabezadosConocidos: a.encabezados });
      onListo();
    } catch (e) {
      toast.addToast(extractErrorMessage(e), 'error');
    } finally {
      setGuardando(false);
    }
  };

  const aviso = `La planilla tiene columnas nuevas: ${columnasNuevas.map(c => c.texto).join(', ')}.`;

  if (!puedeGuardar) {
    return (
      <div className="space-y-4">
        <p className="text-sm text-slate-700">{aviso} Podés ignorarlas por esta vez y seguir con la importación.</p>
        <p className="text-sm text-slate-600">Para que no vuelva a preguntar, alguien con permiso para editar Compras tiene que guardar la decisión.</p>
        <div className="flex justify-end gap-3 border-t pt-3">
          <Button variant="ghost" onClick={onCancelar}>Cancelar</Button>
          <Button onClick={onIgnorar}>Ignorar por esta vez</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-700">{aviso} ¿Qué hacemos con cada una?</p>
      <ul className="space-y-3">
        {columnasNuevas.map(c => (
          <li key={c.indice} className="grid grid-cols-1 items-center gap-2 sm:grid-cols-2">
            <label htmlFor={`cn-${c.indice}`} className="text-sm font-medium">{nombreColumna(c)}</label>
            <select id={`cn-${c.indice}`} value={decision[c.indice] ?? ''} onChange={e => setDecision({ ...decision, [c.indice]: e.target.value as Campo | '' })}
              className="w-full rounded-lg border px-3 py-2 text-sm">
              <option value="">Ignorar</option>
              {CAMPOS.map(campo => <option key={campo} value={campo}>Usar como {ETIQUETA_CAMPO[campo].toLowerCase()}</option>)}
            </select>
          </li>
        ))}
      </ul>
      <p className="text-xs text-slate-500">Lo que elijas queda guardado en el formato del proveedor: la próxima vez no se pregunta.</p>
      <div className="flex justify-end gap-3 border-t pt-3">
        <Button variant="ghost" onClick={onCancelar}>Cancelar</Button>
        <Button onClick={guardar} loading={guardando} disabled={guardando}>
          {guardando ? 'Guardando...' : 'Guardar y seguir'}
        </Button>
      </div>
    </div>
  );
}
